import React, { useCallback, useEffect, useMemo, useState } from "react";
import MsgSendIcon from "@styles/all-images/icons/msgsend.svg";
import closeIcon from "@styles/all-images/icons/close.svg";
import UnreadMsgIcon from "@styles/all-images/icons/unreadmsg.svg";
import UnreadMsgRightIcon from "@styles/all-images/icons/unreadmsgright.svg";
import UserImg from "@styles/all-images/images/Avatar.png";
import logoonwhite from "@styles/all-images/images/logoonlyIbanq.svg";
import PinIcon from "@styles/all-images/icons/pin.svg";
import { ChatUser, Company, Message } from "../types/chat";
import LiveChatFinancialDocument from "./LiveChatFinancialDocument";
import ChatMessages from "../components/ChatMessages";
import { useDispatch } from "react-redux";
import { AppThunkDispatch } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import { useLiveChatState } from "../Provider/LiveChatStateProvider";
import LiveChatUserSkeleton from "../components/skeletons/LiveChatUserSkeleton";
import {
  UserTierType,
  UserTierTypeTextShort,
} from "@/constants/enums/user-tier-type.enum";
import LiveChatFinancialDocumentBackup from "../backup/LiveChatFinancialDocumentBackup";
import { S3Image } from "@/utils/s3-helper";
import UserDropdown from "@styles/all-images/icons/user-dropdown.svg";

interface IndividualChatScreenProps {
  user: ChatUser;
  onDocumentTypeClick: (type: "financial" | "legal") => void;
}

const IndividualChatScreen: React.FC<IndividualChatScreenProps> = ({
  user,
  onDocumentTypeClick,
}) => {
  const dispatch = useDispatch<AppThunkDispatch>();

  const [activeTab, setActiveTab] = useState<"chat" | "financial" | "legal">(
    "chat",
  );
  const [showThread, setShowThread] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const { currentScreen, userInfo, companyInfo, setUserInfo, setCompanyInfo } =
    useLiveChatState();
  const [isUserInfoLoading, setIsUserInfoLoading] = useState(false);

  const getUserInfo = async () => {
    try {
      setIsUserInfoLoading(true);
      const { authenticatedCall } = actionsHandler("getCompanyInfo");
      const response = await dispatch(
        authenticatedCall({
          method: "get",
          customParams: [currentScreen?.data.companyId + "/"],
        }),
      );

      if (response.ok) {
        const userInfo = response.data.companies?.[0]?.users?.find(
          (user: ChatUser) => user.id === currentScreen?.data.user?.id,
        );
        const companyInfo = response.data.companies?.[0];
        setUserInfo(userInfo);
        setActiveTab("chat");
        setCompanyInfo(companyInfo);
        if (companyInfo?.user_type_id !== UserTierType.Tier4) {
          handleTabClick("chat");
        }
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setIsUserInfoLoading(false);
    }
  };

  useEffect(() => {
    if (currentScreen?.data.user && currentScreen?.data.companyId) {
      setShowThread(false);
      getUserInfo();
    }
  }, [currentScreen]);

  const handleTabClick = (tab: "chat" | "financial" | "legal") => {
    setActiveTab(tab);
    if (tab !== "chat") {
      onDocumentTypeClick(tab);
    }
  };

  const handleReplyClick = (message: any) => {
    setSelectedMessage(message);
    if (activeTab === "chat") {
      setShowThread(true);
    }
  };

  const handleCloseThread = () => {
    setShowThread(false);
    setSelectedMessage(null);
  };

  const isThreadVisible = useMemo(() => {
    return showThread && activeTab === "chat";
  }, [showThread, activeTab]);

  useEffect(() => {
    setShowThread(false);
  }, [activeTab]);

  if (isUserInfoLoading) {
    return <LiveChatUserSkeleton />;
  }

  return (
    <div
      className={`tw-grid ${isThreadVisible ? "md:tw-grid-cols-2" : "md:tw-grid-cols-1"} tw-gap-4`}
    >
      <div className="tw-w-full tw-bg-[#FFFFFF] tw-border tw-border-[#E9EAEB] tw-rounded-lg">
        <div>
          <div className="tw-border-b tw-border-[#CBD5E1]">
            <div className="tw-p-4 tw-mx-4 tw-flex tw-items-start tw-justify-between">
              <div className="tw-flex tw-items-center tw-space-x-3">
                <div className="tw-relative">
                  {userInfo?.profile_picture ? (
                    <S3Image
                      src={
                        userInfo?.profile_picture?.replace(
                          "https%3A/9spl-sentinel.s3.us-east-1.amazonaws.com/",
                          "",
                        ) || null
                      }
                      alt="Profile"
                      cacheKey={userInfo?.profile_picture}
                      className="tw-w-10 tw-h-10 tw-rounded-full"
                    />
                  ) : (
                    <img
                      src={UserDropdown}
                      alt="Profile"
                      className="tw-w-10 tw-h-10 tw-rounded-full"
                    />
                  )}
                  <span
                    className={`tw-absolute -tw-top-[2px] -tw-right-[4px] tw-h-[6px] tw-w-[6px] tw-p-[6px] tw-rounded-full ${
                      userInfo?.is_online
                        ? "tw-bg-[#12B76A]"
                        : "tw-bg-[#717680]"
                    } tw-border-2 tw-border-[#FFFFFF]`}
                  ></span>
                </div>
                <div>
                  <h3 className="tw-text-[#232323] tw-text-base tw-font-semibold">
                    {userInfo?.name}
                  </h3>
                  <p className="tw-text-sm tw-text-[#475569] tw-font-normal">
                    {userInfo?.default_profile?.name}
                  </p>
                </div>
              </div>
              <div className="tw-flex tw-items-center tw-gap-4">
                <span className="tw-text-[#809199] tw-text-sm tw-font-medium">
                  {
                    UserTierTypeTextShort[
                      companyInfo?.user_type_id as UserTierType
                    ]
                  }
                </span>
                <button className="tw-flex tw-items-center tw-border tw-border-[#E9EAEB] tw-py-3 tw-px-4 tw-rounded-lg tw-gap-2">
                  <img
                    src={logoonwhite}
                    alt="Lock Icon"
                    className="tw-w-10 tw-h-10 tw-max-w-10 tw-max-h-10 tw-min-w-10 tw-min-h-10"
                  />{" "}
                  {companyInfo?.company_name}
                </button>
              </div>
            </div>
            {(companyInfo?.user_type_id === UserTierType.Tier4 ||
              companyInfo?.user_type_id === UserTierType.Tier3) && (
              <div>
                <div className="tw-flex tw-gap-10 tw-border-b  tw-px-8">
                  <button
                    className={`tw-pb-3 tw-cursor-pointer ${
                      activeTab === "chat"
                        ? "tw-border-b-2 tw-border-[#146C94] tw-text-[#146C94]"
                        : ""
                    }`}
                    onClick={() => handleTabClick("chat")}
                  >
                    Chat
                  </button>
                  <button
                    className={`tw-pb-3 tw-cursor-pointer ${
                      activeTab === "financial"
                        ? "tw-border-b-2 tw-border-[#146C94] tw-text-[#146C94]"
                        : ""
                    }`}
                    onClick={() => handleTabClick("financial")}
                  >
                    Financial Document
                  </button>
                  <button
                    className={`tw-pb-3 tw-cursor-pointer ${
                      activeTab === "legal"
                        ? "tw-border-b-2 tw-border-[#146C94] tw-text-[#146C94]"
                        : ""
                    }`}
                    onClick={() => handleTabClick("legal")}
                  >
                    Legal Document
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="">
            {activeTab === "chat" && (
              <ChatMessages onReplyClick={handleReplyClick} />
            )}

            {(activeTab === "financial" || activeTab === "legal") && (
              // <LiveChatFinancialDocumentBackup type={activeTab} />
              <LiveChatFinancialDocument type={activeTab} />
            )}
          </div>
        </div>
      </div>

      {isThreadVisible && (
        <div className="tw-w-full tw-bg-[#FFFFFF] tw-border tw-border-[#E9EAEB] tw-rounded-lg">
          <div className="tw-mx-4 tw-py-4 tw-border-[#CBD5E1]">
            <div className="tw-flex tw-items-center tw-justify-between">
              <div className="tw-flex tw-items-center tw-space-x-3">
                <img src={PinIcon} alt="Thread" className="tw-w-7 tw-h-7" />
                <h3 className="tw-text-[#232323] tw-text-base tw-font-semibold">
                  Thread
                </h3>
              </div>
              <button onClick={handleCloseThread}>
                <img src={closeIcon} alt="Close" className="tw-w-5 tw-h-5" />
              </button>
            </div>
          </div>

          <ChatMessages threadMessageId={selectedMessage?.id} isThread={true} />
        </div>
      )}
    </div>
  );
};

export default IndividualChatScreen;
